import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type {
  Incident,
  DeclareIncidentRequest,
  AppendIncidentUpdateRequest,
} from './types';

const keys = {
  list: ['incidents'] as const,
  detail: (id: string) => ['incidents', id] as const,
};

export function useIncidentList() {
  return useQuery({
    queryKey: keys.list,
    queryFn: () => apiFetch<Incident[]>('/incidents'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useIncidentDetail(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => apiFetch<Incident>(`/incidents/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useDeclareIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeclareIncidentRequest) =>
      apiFetch<Incident>('/incidents', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useAppendIncidentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AppendIncidentUpdateRequest) =>
      apiFetch<Incident>(`/incidents/${id}/updates`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.list });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function usePatchIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; public_summary?: string; postmortem?: string }) =>
      apiFetch<Incident>(`/incidents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.list });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}

export function useResolveIncident() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Incident>(`/incidents/${id}/resolve`, { method: 'POST' }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: keys.list });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });
}
